<template>
    <div class="card mb-2 page-toolbar">
        <div class="card-body py-2 px-3">
            <div class="d-flex flex-wrap align-items-center">
                <div class="mr-3 my-1 page-toolbar-heading">
                    <!-- a crumb that repeats the title is left out -->
                    <breadcrumb v-if="showBreadcrumb" :omit-title="title" />
                    <h1 class="page-toolbar-title mb-0" v-if="title">
                        {{ title }}
                        <span class="badge badge-light ml-1 align-middle" v-if="count !== null && count !== undefined">{{ count }}</span>
                    </h1>
                </div>

                <div class="d-flex flex-wrap align-items-center ml-auto page-toolbar-actions">
                    <div class="position-relative d-none d-md-block page-toolbar-search" v-if="searchable">
                        <i class="fas fa-search text-muted page-toolbar-search-icon" aria-hidden="true"></i>
                        <input
                            class="form-control form-control-sm page-toolbar-search-input"
                            type="search"
                            :placeholder="searchPlaceholder"
                            :aria-label="searchPlaceholder"
                            :value="searchText"
                            @input="onSearchInput($event.target.value)" />
                    </div>

                    <button
                        v-if="filters.length"
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        :aria-expanded="filtersOpen ? 'true' : 'false'"
                        title="Filters"
                        @click="toggleFilters">
                        <i class="fas fa-filter" aria-hidden="true"></i>
                        <span class="d-none d-md-inline ml-1">Filters</span>
                        <span class="badge badge-primary ml-1" v-if="activeFilters.length">{{ activeFilters.length }}</span>
                    </button>

                    <div class="btn-group" v-if="downloads.length">
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary dropdown-toggle"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                            title="Download">
                            <i class="fas fa-download" aria-hidden="true"></i>
                        </button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <button v-for="kind in downloads" :key="kind" type="button" class="dropdown-item" @click="$emit('download', kind)">
                                <i :class="DOWNLOADS[kind].icon" class="mr-2" aria-hidden="true"></i>{{ DOWNLOADS[kind].label }}
                            </button>
                        </div>
                    </div>

                    <slot name="actions" />
                </div>
            </div>

            <!-- phones: the search gets its own full-width row -->
            <div class="position-relative mt-1 d-md-none" v-if="searchable">
                <i class="fas fa-search text-muted page-toolbar-search-icon" aria-hidden="true"></i>
                <input
                    class="form-control form-control-sm page-toolbar-search-input"
                    type="search"
                    :placeholder="searchPlaceholder"
                    :aria-label="searchPlaceholder"
                    :value="searchText"
                    @input="onSearchInput($event.target.value)" />
            </div>

            <div class="border-top mt-2 pt-2" v-if="filters.length && filtersOpen">
                <div class="form-row">
                    <div class="col-6 col-md-3 mb-2" v-for="filter in filters" :key="filter.key">
                        <label class="small text-muted mb-1" :for="`pt-${uid}-${filter.key}`">{{ filter.label }}</label>
                        <input
                            v-if="filter.type === 'date'"
                            :id="`pt-${uid}-${filter.key}`"
                            type="date"
                            class="form-control form-control-sm"
                            :value="valueKey(filterValues[filter.key])"
                            @change="setFilter(filter.key, $event.target.value)" />
                        <select
                            v-else
                            :id="`pt-${uid}-${filter.key}`"
                            class="custom-select custom-select-sm"
                            :value="valueKey(filterValues[filter.key])"
                            @change="setFilter(filter.key, $event.target.value)">
                            <option value="" v-if="filter.allLabel !== false">{{ filter.allLabel || `All` }}</option>
                            <option v-for="option in filter.options || []" :key="valueKey(option.value)" :value="valueKey(option.value)">
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="text-right" v-if="activeFilters.length">
                    <button type="button" class="btn btn-link btn-sm text-muted p-0" @click="clearFilters">Clear all</button>
                </div>
            </div>

            <div class="mt-1" v-if="activeFilters.length">
                <span class="badge badge-pill badge-soft-primary px-2 py-1 mr-1 mb-1" v-for="chip in activeFilters" :key="chip.key">
                    {{ chip.label }}: {{ chip.valueLabel }}
                    <button type="button" class="btn btn-link p-0 ml-1 align-baseline page-toolbar-chip-x" :aria-label="`Remove ${chip.label} filter`" @click="setFilter(chip.key, '')">
                        <i class="fas fa-times" aria-hidden="true"></i>
                    </button>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, computed, ref, watch, onBeforeUnmount } from "vue";

const DOWNLOADS = Object.freeze({
    xlsx: { label: "Excel", icon: "far fa-file-excel text-success" },
    pdf: { label: "PDF", icon: "far fa-file-pdf text-danger" },
    print: { label: "Print", icon: "fas fa-print" },
});

let toolbarCount = 0;

// The shared page top bar (replaces SearchBar page by page). It loads
// nothing: the page passes its own filter options (from lookups it already
// keeps) and listens for search, filter and download changes.
//
//   <page-toolbar title="Leads" :count="total" searchable v-model:search="term"
//       :filters="[{ key: 'lead_type', label: 'Type', options: [{ value, label }] }]"
//       v-model:filter-values="filters" :downloads="['xlsx', 'pdf']" @download="onDownload">
//       <template #actions>…the page's buttons…</template>
//   </page-toolbar>
export default defineComponent({
    name: "PageToolbar",

    props: {
        title: { type: String, default: "" },
        count: { type: [Number, String], default: null },
        showBreadcrumb: { type: Boolean, default: true },
        // the search box: shown when searchable; v-model:search, debounced
        searchable: { type: Boolean, default: false },
        search: { type: String, default: "" },
        searchPlaceholder: { type: String, default: "Search" },
        searchDelay: { type: Number, default: 350 },
        // [{ key, label, options: [{ value, label }], allLabel? }] (a select; allLabel
        // false hides the "All" option) or [{ key, label, type: "date" }]
        filters: { type: Array, default: () => [] },
        // { [key]: value }; "" or null means unset
        filterValues: { type: Object, default: () => ({}) },
        // any of "xlsx", "pdf", "print"
        downloads: { type: Array, default: () => [] },
    },

    // open-filters: the filters panel opened (load the options then, not on mount)
    emits: ["update:search", "update:filterValues", "download", "open-filters"],

    setup(props, { emit }) {
        toolbarCount += 1;
        const uid = toolbarCount;
        const filtersOpen = ref(false);
        const searchText = ref(props.search);
        let timer = null;

        watch(
            () => props.search,
            (value) => {
                if (value !== searchText.value) searchText.value = value;
            }
        );

        function onSearchInput(value) {
            searchText.value = value;
            clearTimeout(timer);
            timer = setTimeout(() => emit("update:search", value), props.searchDelay);
        }

        onBeforeUnmount(() => clearTimeout(timer));

        function toggleFilters() {
            filtersOpen.value = !filtersOpen.value;
            if (filtersOpen.value) emit("open-filters");
        }

        function valueKey(value) {
            return value === null || value === undefined ? "" : String(value);
        }

        function setFilter(key, value) {
            emit("update:filterValues", { ...props.filterValues, [key]: value });
        }

        function clearFilters() {
            const cleared = {};
            props.filters.forEach((filter) => {
                cleared[filter.key] = "";
            });
            emit("update:filterValues", { ...props.filterValues, ...cleared });
        }

        // a chip per set filter, labelled with the option's text
        const activeFilters = computed(() =>
            props.filters
                .filter((filter) => valueKey(props.filterValues[filter.key]) !== "")
                .map((filter) => {
                    const value = valueKey(props.filterValues[filter.key]);
                    const option = (filter.options || []).find((item) => valueKey(item.value) === value);
                    return { key: filter.key, label: filter.label, valueLabel: option ? option.label : value };
                })
        );

        return {
            DOWNLOADS,
            uid,
            filtersOpen,
            toggleFilters,
            searchText,
            onSearchInput,
            valueKey,
            setFilter,
            clearFilters,
            activeFilters,
        };
    },
});
</script>

<style scoped>
/* Bootstrap 4.5 has no heading size between h5 and h6 that matches the
   app's page titles, and the search input needs a fixed width on desktop. */
.page-toolbar-title {
    font-size: 1.0625rem;
    font-weight: 600;
}

.page-toolbar-search {
    width: 240px;
}

/* the search icon sits inside the field (Bootstrap 4 input groups put it in a
   separate box beside it) */
.page-toolbar-search-icon {
    position: absolute;
    left: 0.6rem;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.75rem;
    pointer-events: none;
}

.page-toolbar-search-input {
    padding-left: 1.9rem;
}

/* one spacing rule for everything on the right, the page's own #actions
   buttons included, so they line up (Bootstrap 4 has no gap utility) */
.page-toolbar-actions > :deep(*) {
    margin: 0.25rem 0 0.25rem 0.5rem;
}

.page-toolbar-chip-x {
    color: inherit;
    font-size: inherit;
    line-height: 1;
}
</style>
